import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What days are you open?",
  "How do referrals work?",
  "What activities do you do?",
  "Are you recruiting?",
];

const getSessionId = () => {
  let id = sessionStorage.getItem("spire-chat-session");
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("spire-chat-session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const data = JSON.parse(payload);
          const chunk = data.delta || data.error || "";
          if (chunk)
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
              return copy;
            });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Sorry, I couldn't answer just now. Please try again, or use our contact page.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat helper" : "Open chat helper — ask us a question"}
        aria-expanded={open}
        className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-stone-900 text-white shadow-xl transition-transform hover:scale-105"
        data-testid="chat-widget-button"
      >
        {open ? <X className="h-6 w-6" aria-hidden="true" /> : <MessageCircle className="h-6 w-6" aria-hidden="true" />}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Spire Helper chat"
          className="fixed bottom-36 right-4 lg:bottom-24 lg:right-6 z-40 flex w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-2xl"
          data-testid="chat-widget-panel"
        >
          <div className="flex items-center gap-3 bg-stone-900 px-5 py-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500/20 text-orange-400">
              <Sparkles className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="font-heading font-semibold text-white leading-tight">Spire Helper</p>
              <p className="text-xs text-stone-400 leading-tight">Ask about our service, visits or careers</p>
            </div>
          </div>

          <div className="flex-1 max-h-80 min-h-[10rem] overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <>
                <p className="text-base text-stone-700">
                  Hello! I can answer questions about Spire Inclusion. Try one of these:
                </p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="rounded-full border-2 border-stone-200 px-3 py-1.5 text-sm text-stone-700 hover:border-orange-600 hover:bg-orange-50"
                      data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-base leading-relaxed ${
                    m.role === "user" ? "bg-orange-600 text-white" : "bg-stone-100 text-stone-800"
                  }`}
                >
                  {m.text || <Loader2 className="h-4 w-4 animate-spin text-stone-500" aria-label="Thinking" />}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 border-t border-stone-100 p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              aria-label="Type your question"
              maxLength={1000}
              className="h-11 w-full rounded-xl border-2 border-stone-200 px-3 text-base focus-visible:border-orange-600"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="Send message"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-40"
              data-testid="chat-send-button"
            >
              <Send className="h-5 w-5" aria-hidden="true" />
            </button>
          </form>
          <p className="px-4 pb-3 text-xs text-stone-400">
            An automated helper — for urgent matters please phone us directly.
          </p>
        </div>
      )}
    </>
  );
};
